import { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import log from "logbeacon";
import { useParams } from "next/navigation";
import { useSnapshot } from "valtio";
import { deviceDetector } from "@ospweb/sdk-ui";
import { QUERY_KEYS } from "../config";
import { OspApiClientContext } from "../features";
import {
  MemberProduct,
  MemberProductResponse,
  PayChannel,
} from "../features/api/memberApi";
import { I18nUtil } from "../utils";
import { rootStore } from "../store";
import {
  ProductPayCycle,
  ProductPlanStatus,
  ProductType,
} from "../components/Member/CurrentPlan/constants";
import { useMemberUser } from "./useMemberUser";
import { getDeviceParser, getDeviceType, getValue } from "utils";
import { REMOTE_CONFIG_KEY_MEMBER_LIFETIME_AND_WEEKLY_PRODUCT } from "../features/firebase/remoteConfigKeys";

const isMemberLifetimeAndWeeklyProductEnabled = async () => {
  try {
    const platform = getDeviceType();
    if (platform === "pc") {
      return true;
    }
    const configValue = (
      await getValue(REMOTE_CONFIG_KEY_MEMBER_LIFETIME_AND_WEEKLY_PRODUCT)
    ).asString();
    const config = JSON.parse(configValue);
    const buildNumber = config[platform];
    const deviceInfo = getDeviceParser();
    log.debug(
      "[useMemberFeature] buildNumber",
      buildNumber,
      deviceInfo,
      config,
      platform,
    );
    return Number(deviceInfo.buildNumber) > buildNumber;
  } catch (error) {
    return false;
  }
};

export const useMemberFeature = () => {
  const { memberApi } = useContext(OspApiClientContext);
  const { locale } = useParams<{ locale: string }>();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const isApp = deviceDetector.isApp();
  const payChannel = deviceDetector.isIOSApp()
    ? PayChannel.APPLE_IAP
    : PayChannel.GOOGLE_IAP;

  const { data: memberUser } = useMemberUser();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      QUERY_KEYS.MEMBER_FEATURE,
      locale,
      isLoggedIn,
      isApp,
      payChannel,
    ],
    enabled: !!memberApi,
    staleTime: 60 * 1000 * 10, // 缓存10 minutes
    queryFn: async () => {
      try {
        // 不传 pay_channels，后端返回全部；前端再按渠道与可用状态筛选
        const res = await memberApi?.member?.listMemberProducts({
          language: I18nUtil.getBackgroundLanguageKey(locale),
        });
        const resData = res?.data as MemberProductResponse;
        log.info("[Member][trace]getMemberProducts", resData);
        if (resData?.msg === "success" && !!resData?.data) {
          const enableLifetimeAndWeeklyProduct =
            await isMemberLifetimeAndWeeklyProductEnabled();
          if (enableLifetimeAndWeeklyProduct) {
            return resData?.data ?? [];
          }
          return resData?.data.filter(
            (item) =>
              ![ProductPayCycle.WEEK, ProductPayCycle.LIFETIME].includes(
                item.pay_cycle as ProductPayCycle,
              ),
          );
        }
        return [];
      } catch (error) {
        log.debug("[Member][trace]getMemberProducts error", error);
        return [];
      }
    },
    select(data: MemberProduct[]) {
      const list = Array.isArray(data) ? data : [];
      let trialList: MemberProduct[] = [];
      // 1. 先从 memberUser.available_products 中拿到所有 trial 产品对应的 product_id 列表
      const trialProductIds =
        memberUser?.available_products
          ?.filter((item: any) => item.product_type === "trial")
          ?.map((item: any) => item.product_id)
          ?.filter(Boolean) ?? [];

      // 2. 再基于总的 member 产品列表按平台和类型筛选出真正可用的试用产品
      if (trialProductIds.length > 0) {
        if (isApp) {
          // App 端：只保留当前端对应 IAP 渠道的 ACTIVE trial 产品
          trialList = list.filter(
            (item: MemberProduct) =>
              trialProductIds.includes(item.id) &&
              item.payment_configs?.some(
                (c) => c.channel === payChannel && c.status === "ACTIVE",
              ) &&
              item.type === ProductType.TRIAL &&
              item.status === ProductPlanStatus.ACTIVE,
          );
        } else {
          // Web 端：只保留非 IAP 渠道、ACTIVE 的 trial 产品
          trialList = list.filter(
            (item: MemberProduct) =>
              trialProductIds.includes(item.id) &&
              item.payment_configs?.some(
                (c) =>
                  ![PayChannel.APPLE_IAP, PayChannel.GOOGLE_IAP].includes(
                    c.channel as PayChannel,
                  ) && c.status === "ACTIVE",
              ) &&
              item.type === ProductType.TRIAL &&
              item.status === ProductPlanStatus.ACTIVE,
          );
        }
      }
      // 是否存在试用产品
      const isTrial = trialList.length > 0;
      const newData = list.map((item: MemberProduct) => {
        const usdPrice = item.currencies?.find(
          (currency: any) => currency.currency === "USD",
        )?.price;

        return {
          ...item,
          price: usdPrice,
        };
      });

      return {
        data: newData,
        isTrial,
        trialList,
      };
    },
  });

  return {
    data: data?.data || [],
    isLoading,
    isTrial: data?.isTrial,
    trialList: data?.trialList,
    refetch,
  };
};
